import { useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { HiOutlineDocumentText } from "react-icons/hi";
import {
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Link,
  Select,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { usePartyNames } from "../../hooks/usePartyNames";
import { carrierInbox, quarterStart } from "./placingBook";
import { DueDate, relativeTime, shortDate } from "./PlacingSummaryParts";
import PortalStageBlocks from "./PortalStageBlocks";
import { isNewSince, useSeenStages } from "../../hooks/useSeenStages";
import PlacingStatusBadge from "./PlacingStatusBadge";
import { RoleBadge } from "./LineBadges";
import { ReasonText } from "./ReasonFields";

const TYPE_COLOURS = {
  "Quote request": "blue",
  "Line request": "teal",
  "Signed line": "green",
  "Signed line advice": "green",
  Submission: "gray",
  "Quote sent": "gray",
  "Offer sent": "gray",
  Nominated: "green",
  "Not taken up": "orange",
  Declined: "red",
};

const STAGE_BLOCKS = [
  { key: "build", title: "Build", unit: ["submission", "submissions"] },
  { key: "quotation", title: "Quotation", unit: ["quote request", "quote requests"] },
  { key: "order", title: "Order", unit: ["line request", "line requests"] },
  { key: "signing", title: "Signing", unit: ["signed line", "signed lines"] },
];

/** Everything brokers have sent to the user's carrier parties, and what they've sent back. */
const CarrierPlacingInbox = ({ records, mine, myPartyIds = [] }) => {
  const navigate = useNavigate();
  const [stage, setStage] = useState(null); // a stage key, or null for every stage
  const [needsOnly, setNeedsOnly] = useState(true);
  const [search, setSearch] = useState("");
  const [broker, setBroker] = useState("");
  const [type, setType] = useState("");
  const { seenAt, sinceLastVisit, markSeen } = useSeenStages("carrier");

  const rows = useMemo(() => carrierInbox(records, mine), [records, mine]);
  // New: the broker sent or decided something since this stage was last opened
  const isNew = (r) => isNewSince(sinceLastVisit, r.stage, r.newAt);
  const names = usePartyNames([...rows.map((r) => r.fromPartyId), ...rows.map((r) => r.asPartyId), ...myPartyIds]);

  const blocks = STAGE_BLOCKS.map(({ key, title, unit }) => {
    const here = rows.filter((r) => r.stage === key);
    const count = (test) => here.filter(test).length;
    const detail = {
      build: `${count((r) => r.asked === "You declared interest")} interested · ${count((r) => r.asked === "You declined to participate")} declined`,
      quotation: `${count((r) => r.type === "Quote sent")} quoted · ${count((r) => r.type === "Nominated")} nominated · ${count((r) => r.type === "Declined")} declined`,
      order: `${count((r) => r.status === "offered")} written · ${count((r) => r.status === "declined")} declined`,
      signing: `${count((r) => new Date(r.received || 0) >= quarterStart())} this quarter`,
    }[key];
    return {
      key,
      title,
      count: here.length,
      unit: unit[here.length === 1 ? 0 : 1],
      needs: count((r) => r.tab === "needs"),
      newCount: count((r) => isNewSince(seenAt, key, r.newAt)),
      detail,
    };
  });

  const selectStage = (key) => {
    setStage(key);
    // A stage shows everything in it; back to all stages shows what needs you
    setNeedsOnly(!key);
    if (key) markSeen(key);
  };

  const brokers = [...new Set(rows.map((r) => r.fromPartyId).filter(Boolean))];
  const types = [...new Set(rows.filter((r) => !stage || r.stage === stage).map((r) => r.type))];

  const shown = rows
    .filter((r) => !stage || r.stage === stage)
    .filter((r) => !needsOnly || r.tab === "needs" || isNew(r))
    .filter((r) => !broker || r.fromPartyId === broker)
    .filter((r) => !type || r.type === type)
    .filter((r) => {
      const text = search.trim().toLowerCase();
      return !text || [r.riskName, names[r.fromPartyId], names[r.asPartyId], r.asked].some((v) => (v || "").toLowerCase().includes(text));
    });

  const partyList = myPartyIds.map((id) => names[id]).filter(Boolean).join(", ");

  return (
    <VStack align="stretch" spacing={5}>
      <Box>
        <Heading size="lg">Placing inbox</Heading>
        <Text color="gray.600">
          Everything brokers have sent to {partyList || "your organisation"}, and what you have sent back
        </Text>
      </Box>

      <PortalStageBlocks blocks={blocks} selected={stage} onSelect={selectStage} />

      <Box borderWidth="1px" borderRadius="md" bg="whiteAlpha.800">
        <HStack px={4} py={2} justify="space-between" flexWrap="wrap" rowGap={2}>
          <HStack spacing={4}>
            <Heading size="sm">{stage ? STAGE_BLOCKS.find((b) => b.key === stage).title : "All stages"}</Heading>
            <FormControl display="flex" alignItems="center" w="auto">
              <Switch id="carrier-needs-only" size="sm" colorScheme="orange" isChecked={needsOnly} onChange={(e) => setNeedsOnly(e.target.checked)} />
              <FormLabel htmlFor="carrier-needs-only" mb={0} ml={2} fontSize="sm">Needs you or new only</FormLabel>
            </FormControl>
          </HStack>
          <HStack spacing={2}>
            <Input size="sm" w="240px" placeholder="Search risk, broker, request…" value={search} onChange={(e) => setSearch(e.target.value)} />
            <Select size="sm" w="150px" value={broker} onChange={(e) => setBroker(e.target.value)}>
              <option value="">All brokers</option>
              {brokers.map((id) => <option key={id} value={id}>{names[id] || "…"}</option>)}
            </Select>
            <Select size="sm" w="170px" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">All message types</option>
              {types.map((t) => <option key={t} value={t}>{t}</option>)}
            </Select>
          </HStack>
        </HStack>

        {shown.length === 0 ? (
          <Text p={6} fontSize="sm" color="gray.500">
            {needsOnly ? "Nothing needs you. Click a stage above to see everything in it." : "Nothing here."}
          </Text>
        ) : (
          <Table size="sm">
            <Thead>
              <Tr><Th>Received</Th><Th>Risk</Th><Th>From</Th><Th>As</Th><Th>Your role</Th><Th>Asked of you</Th><Th>Respond by</Th><Th /></Tr>
            </Thead>
            <Tbody>
              {shown.map((r) => (
                <Tr key={r.key} bg={r.tab === "needs" ? "orange.50" : undefined}>
                  <Td>
                    <HStack spacing={1}>
                      <Badge colorScheme={TYPE_COLOURS[r.type] || "gray"} borderRadius="full" px={2} textTransform="none">{r.type}</Badge>
                      {r.type === "Line request" && <PlacingStatusBadge status={r.status} fontSize="0.6em" />}
                      {r.type === "Signed line advice" && <PlacingStatusBadge status="signed" fontSize="0.6em" variant="solid" />}
                      {isNew(r) && <Badge colorScheme="red" variant="solid" borderRadius="full" fontSize="0.6em">NEW</Badge>}
                    </HStack>
                    <Text fontSize="xs" color="gray.500">{relativeTime(r.received)}</Text>
                  </Td>
                  <Td>
                    {r.documentId ? (
                      <Tooltip label="Open the contract document" hasArrow openDelay={300}>
                        <Link as={RouterLink} to={`/editor?doc=${r.documentId}`} fontWeight="semibold" color="blue.700">{r.riskName}</Link>
                      </Tooltip>
                    ) : (
                      <Text fontWeight="semibold">{r.riskName}</Text>
                    )}
                    {/* A party can be sent several quote (or line) requests on one submission */}
                    {(r.requestNumber || r.type === "Line request") && (
                      <Text fontSize="xs" color="gray.500">
                        {r.requestNumber?.of > 1 && `Request ${r.requestNumber.index} of ${r.requestNumber.of} · `}
                        sent {shortDate(r.sentDate)}
                        {r.requestedShare != null && ` · ${r.requestedShare}% requested`}
                      </Text>
                    )}
                  </Td>
                  <Td>{names[r.fromPartyId] || "—"}</Td>
                  <Td>{r.asPartyId ? names[r.asPartyId] || "…" : "—"}</Td>
                  <Td>{r.lineRole ? <RoleBadge role={r.lineRole} /> : r.role}</Td>
                  <Td>
                    {r.asked}
                    {r.reason && <ReasonText color={r.reason.ntu ? "orange.700" : "red.600"} code={r.reason.code} notes={r.reason.notes} />}
                  </Td>
                  <Td>
                    {r.respondByLabel && r.respondBy ? (
                      <Text>{r.respondByLabel} {shortDate(r.respondBy)}</Text>
                    ) : (
                      <DueDate value={r.respondBy} isOpen={r.tab === "needs"} />
                    )}
                  </Td>
                  <Td textAlign="right" whiteSpace="nowrap">
                    {r.documentId && (
                      <Tooltip label="Open the contract document" hasArrow>
                        <IconButton size="xs" variant="ghost" mr={1} aria-label="Open the contract document" icon={<HiOutlineDocumentText />}
                          onClick={() => navigate(`/editor?doc=${r.documentId}`)} />
                      </Tooltip>
                    )}
                    {r.link ? (
                      <Button
                        size="xs"
                        colorScheme={r.actionLabel === "Respond" ? "teal" : undefined}
                        variant={r.actionLabel === "Respond" ? "solid" : "outline"}
                        onClick={() => navigate(r.link)}>
                        {r.actionLabel}
                      </Button>
                    ) : (
                      <Tooltip label="Order screens arrive with the Order process" hasArrow>
                        <Button size="xs" variant="outline" isDisabled>{r.actionLabel}</Button>
                      </Tooltip>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

    </VStack>
  );
};

export default CarrierPlacingInbox;
